import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PwaInstallProvider } from "./context/PwaInstallContext";
import SaveToPhoneModal from "./components/SaveToPhoneModal";
import PwaAnalyticsBridge from "./components/PwaAnalyticsBridge";
import AppShell from "./components/AppShell";
import AuthPage from "./pages/AuthPage";
import HomePage from "./pages/HomePage";
import CoachPage from "./pages/CoachPage";
import GrowthPage from "./pages/GrowthPage";
import ProfilePage from "./pages/ProfilePage";
import AnalyzePage from "./pages/AnalyzePage";
import BiblePage from "./pages/BiblePage";
import DecisionPage from "./pages/DecisionPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import SSOPage from "./pages/SSOPage";
import SSOErrorPage from "./pages/SSOErrorPage";
import GiftClaimPage from "./pages/GiftClaimPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import GiftLandingPage from "./pages/GiftLandingPage";
import GiftSuccessPage from "./pages/GiftSuccessPage";
import WelcomePage from "./pages/WelcomePage";
import GuestPage from "./pages/GuestPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import JournalPage from "./pages/JournalPage";
import WeeklyReflectionPage from "./pages/WeeklyReflectionPage";
import SavedWisdomPage from "./pages/SavedWisdomPage";
import TestLoginPage from "./pages/TestLoginPage";
import SmsOptInPage from "./pages/SmsOptInPage";
import ContactPage from "./pages/ContactPage";
import SponsorLandingPage from "./pages/SponsorLandingPage";
import SponsorInviteAcceptPage from "./pages/SponsorInviteAcceptPage";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminSponsorsListPage from "./pages/admin/AdminSponsorsListPage";
import AdminSponsorManagePage from "./pages/admin/AdminSponsorManagePage";
import AdminOverviewPage from "./pages/admin/AdminOverviewPage";
import AdminProverbAnalyticsPage from "./pages/admin/AdminProverbAnalyticsPage";
import AdminProverbCardOrdersPage from "./pages/admin/AdminProverbCardOrdersPage";
import AdminLifeGuideAnalyticsPage from "./pages/admin/AdminLifeGuideAnalyticsPage";
import AdminSponsorAnalyticsPage from "./pages/admin/AdminSponsorAnalyticsPage";
import AdminRevenuePage from "./pages/admin/AdminRevenuePage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminEngagementPage from "./pages/admin/AdminEngagementPage";
import AdminGiftsPage from "./pages/admin/AdminGiftsPage";
import AdminSubscriptionsPage from "./pages/admin/AdminSubscriptionsPage";
import AdminSmsPage from "./pages/admin/AdminSmsPage";
import AdminTrialRemindersPage from "./pages/admin/AdminTrialRemindersPage";
import AdminSystemHealthPage from "./pages/admin/AdminSystemHealthPage";
import AdminShell from "./components/AdminShell";
import AdminLoginPage from "./pages/AdminLoginPage";
import SponsorDashboardPage from "./pages/sponsor/SponsorDashboardPage";
import SponsorPortalHome from "./pages/sponsor/SponsorPortalHome";
import SponsorLoginPage from "./pages/sponsor/SponsorLoginPage";
import { adminApi } from "./lib/api";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#6B6B6B]">
        Loading...
      </div>
    );
  }
  // Unauth'd visitors — including everyone coming from I Need a Proverb —
  // are dropped into the free public preview at /welcome, NOT the login
  // page. The welcome page has a "Sign in" link in its header for
  // returning users. This is the "no account required" guest onramp.
  if (!user) return <Navigate to="/welcome" replace />;
  return children;
};

// Master admin gate — auth-required + verified via /api/admin/whoami.
// Renders a friendly 403 if the current user is not a master admin.
const AdminProtected = ({ children }) => {
  const { user, loading, logout } = useAuth();
  const [state, setState] = React.useState({ checking: true, isAdmin: false });
  React.useEffect(() => {
    if (!user) return;
    adminApi
      .whoami()
      .then((r) => setState({ checking: false, isAdmin: !!r.is_master_admin }))
      .catch(() => setState({ checking: false, isAdmin: false }));
  }, [user]);
  if (loading || (user && state.checking)) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#6B6B6B]">
        Loading...
      </div>
    );
  }
  // Unauthenticated → dedicated admin login (never the consumer /login).
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!state.isAdmin) {
    return (
      <div
        data-testid="admin-forbidden"
        className="min-h-screen bg-[#0B1210] flex items-center justify-center px-6"
      >
        <div className="max-w-md w-full rounded-3xl border border-[#7C3E33]/50 bg-[#1A100E] p-8 text-center">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#E38680] mb-3">
            Access Denied
          </div>
          <h1 className="font-display text-3xl text-white mb-2">
            You do not have admin access
          </h1>
          <p className="text-sm text-[#B7D2AF] leading-relaxed mb-6">
            Signed in as <span className="text-white">{user.email}</span>. This
            account is not authorized to view the Master Admin area.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              data-testid="admin-forbidden-signout"
              onClick={() => {
                logout();
                window.location.assign("/admin/login");
              }}
              className="w-full sm:w-auto bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-6 py-2.5 text-sm font-medium"
            >
              Sign in with a different account
            </button>
            <a
              href="/"
              data-testid="admin-forbidden-home"
              className="text-sm text-[#B7D2AF] hover:text-white"
            >
              Back to LifeGuide
            </a>
          </div>
        </div>
      </div>
    );
  }
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <PwaInstallProvider>
          <BrowserRouter>
            <Toaster position="top-center" richColors closeButton />
            <PwaAnalyticsBridge />
            <SaveToPhoneModal />
          <Routes>
            <Route path="/login" element={<AuthPage mode="login" />} />
            <Route path="/signup" element={<AuthPage mode="signup" />} />
            <Route path="/sso" element={<SSOPage />} />
            <Route path="/sso/error" element={<SSOErrorPage />} />
            <Route path="/gift/claim/:token" element={<GiftClaimPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/gift" element={<GiftLandingPage />} />
            <Route path="/gift/success" element={<GiftSuccessPage />} />
            <Route path="/welcome" element={<WelcomePage />} />
            <Route path="/guest" element={<GuestPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/test-login" element={<TestLoginPage />} />
            <Route path="/sms" element={<SmsOptInPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/sponsor/:slug" element={<SponsorLandingPage />} />
            <Route path="/sponsor-invite/:token" element={<SponsorInviteAcceptPage />} />
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/sponsor" element={<SponsorPortalHome />} />
            <Route path="/sponsor/login" element={<SponsorLoginPage />} />
            <Route
              element={
                <AdminProtected>
                  <AdminShell />
                </AdminProtected>
              }
            >
              <Route path="/admin" element={<AdminOverviewPage />} />
              <Route path="/admin/proverb" element={<AdminProverbAnalyticsPage />} />
              <Route path="/admin/proverb/card-orders" element={<AdminProverbCardOrdersPage />} />
              <Route path="/admin/lifeguide" element={<AdminLifeGuideAnalyticsPage />} />
              <Route path="/admin/engagement" element={<AdminEngagementPage />} />
              <Route path="/admin/sponsors-analytics" element={<AdminSponsorAnalyticsPage />} />
              <Route path="/admin/revenue" element={<AdminRevenuePage />} />
              <Route path="/admin/gifts" element={<AdminGiftsPage />} />
              <Route path="/admin/subscriptions" element={<AdminSubscriptionsPage />} />
              <Route path="/admin/sms" element={<AdminSmsPage />} />
              <Route path="/admin/trial-reminders" element={<AdminTrialRemindersPage />} />
              <Route path="/admin/system-health" element={<AdminSystemHealthPage />} />
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/sponsors" element={<AdminSponsorsListPage />} />
              <Route path="/admin/sponsors/:sponsor_id/manage" element={<AdminSponsorManagePage />} />
              <Route path="/admin/legacy" element={<AdminDashboardPage />} />
            </Route>
            <Route
              element={
                <Protected>
                  <AppShell />
                </Protected>
              }
            >
              <Route path="/sponsor-dashboard/:sponsor_id" element={<SponsorDashboardPage />} />
            </Route>
            <Route
              path="/payment/success"
              element={
                <Protected>
                  <PaymentSuccessPage />
                </Protected>
              }
            />
            <Route
              element={
                <Protected>
                  <AppShell />
                </Protected>
              }
            >
              <Route path="/" element={<HomePage />} />
              <Route path="/coach" element={<CoachPage />} />
              <Route path="/journal" element={<JournalPage />} />
              <Route path="/journal/weekly-reflection" element={<WeeklyReflectionPage />} />
              <Route path="/saved" element={<SavedWisdomPage />} />
              <Route path="/growth" element={<GrowthPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/analyze" element={<AnalyzePage />} />
              <Route path="/bible" element={<BiblePage />} />
              <Route path="/decision" element={<DecisionPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </PwaInstallProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
